import { useEffect, useState } from "react";
import { Carousel, Text } from "@ttc3k/trekker";
import { getValueImages } from "shared/merchCatalog";
import { useTranslation } from "react-i18next";
import { limitFill } from "@cloudinary/url-gen/actions/resize";
import { autoGravity } from "@cloudinary/transformation-builder-sdk/qualifiers/gravity";
import { AssetImage, Product } from "gql/generated";
import { CloudinaryImage } from "shared/components";
import { formatPrice, getMultilingualString } from "shared/utils";
import { css } from "styled-system/css";
import { Box, Flex } from "styled-system/jsx";
import { useBookerContext } from "widgets/booker/contexts";
import { OverlayPage } from "../OverlayPages/OverlayPage";
import { MerchVariantOptions } from "../Steps/components/MerchVariantSelection";
import { getCatalogProductSummary } from "./merchCatalogUtils";
import { STORE_FROZEN_BACKDROP_STYLE } from "./catalogPaneStyles";

const IMAGE_HEIGHT = 340;

type MerchProductOverlayProps = {
  product: Product | null;
  open: boolean;
  /** Opens the gallery on this value's first photo (e.g. the colour dot that was hovered). */
  initialValueId?: string | null;
  onClose: () => void;
  cartVariantQuantities: Record<string, number>;
};

export const MerchProductOverlay = ({
  product,
  open,
  initialValueId,
  onClose,
  cartVariantQuantities,
}: MerchProductOverlayProps) => {
  const { language } = useBookerContext();
  const { t } = useTranslation("booker", { keyPrefix: "MERCH_CATALOG" });
  const [imagePage, setImagePage] = useState(0);

  useEffect(() => {
    if (!open) return;
    const first = getValueImages(product, initialValueId)[0];
    const index = first
      ? (product?.images ?? []).findIndex(image => image?._id === first._id)
      : -1;
    setImagePage(Math.max(index, 0));
  }, [open, product, initialValueId]);

  if (!product) return null;

  const { lowestPrice, hasPriceRange, quantityInCart } =
    getCatalogProductSummary(product, cartVariantQuantities);
  const images = (product.images ?? []).filter(Boolean) as AssetImage[];
  const description = getMultilingualString(product.description ?? {}, language);

  const showValueImages = (valueId: string) => {
    const first = getValueImages(product, valueId)[0];
    const index = first ? images.findIndex(image => image._id === first._id) : -1;
    if (index >= 0) setImagePage(index);
  };

  return (
    <OverlayPage
      open={open}
      onClose={onClose}
      title={getMultilingualString(product.name, language)}
      subtitle={
        hasPriceRange
          ? t("FROM", { price: `$${formatPrice(lowestPrice)}` })
          : `$${formatPrice(lowestPrice)}`
      }
      buttonLabel={
        quantityInCart > 0
          ? t("DONE_IN_CART", { count: quantityInCart })
          : t("BACK_TO_STORE")
      }
      onButtonClick={onClose}
      backdropProps={{ style: STORE_FROZEN_BACKDROP_STYLE }}
    >
      <Flex flexDirection="column" gap="400" mt="300">
        {images.length > 0 && (
          <Box borderRadius="200" overflow="hidden">
            <Carousel
              images={images}
              height={IMAGE_HEIGHT}
              page={Math.min(imagePage, images.length - 1)}
              onPageChange={({ page }: { page: number }) => setImagePage(page)}
              render={(image: Partial<AssetImage>) => (
                <CloudinaryImage
                  image={image}
                  alt={getMultilingualString(product.name, language)}
                  imageStyleProps={{
                    className: css({
                      w: "full",
                      h: `${IMAGE_HEIGHT}px`,
                      objectFit: "cover",
                    }),
                    aspectRatio: "unset",
                  }}
                  resize={limitFill().height(IMAGE_HEIGHT * 2).gravity(autoGravity())}
                />
              )}
            />
          </Box>
        )}
        {description && (
          <Text color="text.mid" whiteSpace="pre-line">
            {description}
          </Text>
        )}
        <MerchVariantOptions
          product={product}
          onImageValueChange={showValueImages}
        />
      </Flex>
    </OverlayPage>
  );
};
